import { useState, useMemo, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { X, Search } from 'react-feather';
import { nanoid } from 'nanoid';
import Text from 'atoms/Text';
import Tab from 'molecules/Tab';
import CommandHistoryTile from 'molecules/CommandHistoryTile';
import CommandFilterPills, { ALL } from 'molecules/CommandFilterPills';
import { toRailLabel } from 'lib/commandFormatRegistry';
import { groupByDate } from 'helper/dateGrouping';
import { popupSpecsForDes } from 'lib/constants';
import commandHistoryStore from 'store/commandHistoryStore';
import useCloseOnOutsideClick from 'hooks/useCloseOnOutsideClick';

// The HIST/FAVS rail buttons live outside the panel but already toggle it
// themselves, so an outside click must leave them alone — otherwise mousedown
// would close the panel and the following click would reopen it.
const RAIL_TOGGLES = '[data-rail-command="HIST"],[data-rail-command="FAVS"]';

const keyOf = (e) => `${toRailLabel(e.command)}||${e.curve || ''}||${e.structure || ''}`;

const EmptyState = ({ message }) => (
    <div className="text-center mt-16 px-4">
        <img src="/images/icons/bird.svg" alt="" className="mx-auto mb-2" />
        <Text variant="bodySmall" className="text-gray-200">{message}</Text>
    </div>
);

EmptyState.propTypes = { message: PropTypes.node.isRequired };

const CommandHistoryPanel = ({ isOpen, onClose, initialTab, openToken }) => {
    const entries = commandHistoryStore((s) => s.entries);
    const loadHistory = commandHistoryStore((s) => s.loadHistory);
    const toggleFavourite = commandHistoryStore((s) => s.toggleFavourite);

    const [activeTab, setActiveTab] = useState(initialTab);
    const [activePill, setActivePill] = useState(ALL);
    const [search, setSearch] = useState('');
    const [error, setError] = useState('');
    const panelRef = useRef(null);

    useCloseOnOutsideClick({ isOpen, panelRef, onClose, ignoreSelector: RAIL_TOGGLES });

    // Every HIST/FAVS rail click bumps `openToken`, so this fires on each click
    // — whether opening the panel or re-selecting a tab while it is already open.
    // Tab/filter/search reset to defaults (Story §5) and history is (re)loaded.
    useEffect(() => {
        if (!isOpen) return;
        setActiveTab(initialTab);
        setActivePill(ALL);
        setSearch('');
        setError('');
        loadHistory();
    }, [openToken, isOpen, initialTab, loadHistory]);

    const favourites = useMemo(() => {
        const seen = new Set();
        return entries.filter((e) => {
            if (!e.isFavourite) return false;
            const k = keyOf(e);
            if (seen.has(k)) return false;
            seen.add(k);
            return true;
        });
    }, [entries]);

    const visible = useMemo(() => {
        const source = activeTab === 'favourites' ? favourites : entries;
        const q = search.trim().toLowerCase();
        return source.filter((e) => {
            const pillOk = activePill === ALL || toRailLabel(e.command) === activePill;
            if (!pillOk) return false;
            if (!q) return true;
            return toRailLabel(e.command).toLowerCase().includes(q)
                || (e.line2 || '').toLowerCase().includes(q);
        });
    }, [activeTab, entries, favourites, activePill, search]);

    const groups = useMemo(() => groupByDate(visible), [visible]);

    const reopen = (entry) => {
        const url = entry?.snapshot?.url;
        if (!url) {
            setError('This command can no longer be opened — its curve may have been retired.');
            return;
        }
        window.open(url, `win_${nanoid(8)}`, popupSpecsForDes);
        onClose();
    };

    const sourceEmpty = (activeTab === 'favourites' ? favourites : entries).length === 0;
    const emptyMessage = activeTab === 'favourites'
        ? (
            <>
                No Favorite Commands
                <br />
                Add commands to Favorites from the Recent tab
                for quick access
            </>
        )
        : 'No commands yet. Run a command and it will appear here.';

    return (
        <div
            ref={panelRef}
            className={`
                absolute top-0 left-full h-screen overflow-hidden z-50
                transition-[width] duration-300 ease-in-out
                ${isOpen ? 'w-[300px]' : 'w-0'}
            `}
        >
            <div className="w-[300px] h-screen bg-gray-900 border-r border-gray-600 flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between px-4 border-b border-gray-600">
                    <Tab
                        key={openToken}
                        tabNavClassName="!mb-0 !border-b-0"
                        tabBodyclassname="hidden"
                        tabs={[
                            {
                                id: 'recent',
                                title: 'Recent',
                                theme: 'agy',
                                defaultTab: initialTab === 'recent',
                                onClick: () => setActiveTab('recent')
                            },
                            {
                                id: 'favourites',
                                title: 'Favourites',
                                theme: 'agy',
                                defaultTab: initialTab === 'favourites',
                                onClick: () => setActiveTab('favourites')
                            }
                        ]}
                    />
                    <X
                        size={16}
                        className="text-gray-200 hover:text-gray-100 cursor-pointer"
                        onClick={onClose}
                    />
                </div>

                {/* Search + pills */}
                <div className="p-3 border-b border-gray-700 space-y-2">
                    <div className="h-8 flex items-center gap-1 px-3 rounded-md border border-gray-600">
                        <Search size={16} className="text-gray-200" />
                        <input
                            placeholder="Search commands..."
                            className="bg-transparent outline-none text-gray-100 text-xs placeholder:text-gray-200 w-full"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>
                    <CommandFilterPills active={activePill} onChange={setActivePill} />
                </div>

                {/* List */}
                <div className="overflow-y-auto h-[calc(100vh-150px)] p-3 space-y-3">
                    {error && (
                        <div className="bg-error-300/10 border border-error-300/20 rounded px-2.5 py-2">
                            <Text variant="small" className="text-error-300">{error}</Text>
                        </div>
                    )}

                    {sourceEmpty && <EmptyState message={emptyMessage} />}

                    {!sourceEmpty && groups.length === 0 && (
                        <EmptyState message="No results match your search." />
                    )}

                    {groups.map((group) => (
                        <div key={group.label} className="space-y-2">
                            <Text variant="small" className="text-gray-200 uppercase" fontWeight="font-semibold">
                                {group.label}
                            </Text>
                            {group.items.map((entry) => (
                                <CommandHistoryTile
                                    key={entry.id}
                                    entry={entry}
                                    isFavourite={Boolean(entry.isFavourite)}
                                    onReopen={reopen}
                                    onToggleFavourite={(e) => toggleFavourite({ command: e.command, curve: e.curve, structure: e.structure })}
                                />
                            ))}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

CommandHistoryPanel.propTypes = {
    isOpen: PropTypes.bool,
    onClose: PropTypes.func.isRequired,
    initialTab: PropTypes.oneOf(['recent', 'favourites']),
    openToken: PropTypes.number
};

CommandHistoryPanel.defaultProps = {
    isOpen: false,
    initialTab: 'recent',
    openToken: 0
};

export default CommandHistoryPanel;
